import type { PropType } from 'vue';
import type { TDialogProps } from './TDialog.types';
import { merge } from 'es-toolkit/compat';
import { computed, defineComponent, h } from 'vue';
import { toCamelCase } from '../../utils';
import { TButton } from '../TButton';
import { TIcon } from '../TIcon';
import { TModal } from '../TModal';
import { TTypography } from '../TTypography';
import { useStyles } from './TDialog.styles';

const defaultProps = {
  cancelText: 'Cancel',
  confirmText: 'OK',
};

export const TDialog = defineComponent({
  name: 'TDialog',
  components: {
    TTypography,
    TModal,
    TButton,
    TIcon,
  },
  props: {
    title: {
      type: String as PropType<TDialogProps['title']>,
      required: true,
    },
    message: {
      type: String as PropType<TDialogProps['message']>,
      required: true,
    },
    color: String as PropType<TDialogProps['color']>,
    icon: String as PropType<TDialogProps['icon']>,
    cancellable: Boolean as PropType<TDialogProps['cancellable']>,
    cancelText: String as PropType<TDialogProps['cancelText']>,
    confirmText: String as PropType<TDialogProps['confirmText']>,
  },
  emits: ['confirm', 'cancel', 'close'],
  setup(_props, { emit }) {
    const props = computed(() => merge({}, defaultProps, _props));

    const baseClass = useStyles();

    const iconColor = computed(() => {
      if (props.value.color) {
        return props.value.color === 'primary'
          ? 'primary.main'
          : toCamelCase(props.value.color);
      }
      return undefined;
    });

    return () => h(
      TModal,
      {
        onClose: () => props.value.cancellable && emit('close'),
        onCancel: () => props.value.cancellable && emit('cancel'),
        onConfirm: () => emit('confirm'),
        class: baseClass.value,
        open: true,
      },
      {
        header: () => h(
          TTypography,
          { variant: 's2', fontWeight: 'semibold' },
          () => props.value.title,
        ),
        default: () => h('div', { class: `${baseClass.value}__body` }, [
          props.value.icon
            ? h(TIcon, {
                class: `${baseClass.value}__body-icon`,
                variant: 'solid',
                color: iconColor.value,
                icon: props.value.icon,
              })
            : undefined,
          h(TTypography, {
            class: `${baseClass.value}__body-text`,
            variant: 'b1',
            innerHTML: props.value.message,
          }),
        ]),
        footer: () => [
          props.value.cancellable
            ? h(
                TButton,
                {
                  onClick: () => emit('cancel'),
                  color: props.value.color,
                  variant: 'secondary',
                },
                () => props.value.cancelText,
              )
            : undefined,
          h(
            TButton,
            {
              onClick: () => emit('confirm'),
              color: props.value.color,
            },
            () => props.value.confirmText,
          ),
        ],
      },
    );
  },
});
